/**
 * Avatar — circular user/guest mark.
 *
 * Renders an <img> when `src` is provided; otherwise shows initials from `name`
 * (first letter of each of the first two words, uppercased) on a gold wash.
 * Optional gold ring and a presence status dot (online / away / offline).
 *
 * Re-authored from ../design-system/components/data-display/Avatar.jsx
 * onto Tailwind v4 / cn() / no injected styles.
 *
 * NOTE: no focus-trap or initial-focus management — matches DS; full a11y
 * polish is deferred to R5.
 */
import { type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  src?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  ring?: boolean;
  status?: 'online' | 'away' | 'offline';
}

/** Extract up to 2 initials from a name string. */
function initials(name = ''): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

// Sizes match DS px values (xs 24px → h-6/w-6, sm 32px, md 40px, lg 56px, xl 80px)
const SIZE_CLASS: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-lg',
  xl: 'h-20 w-20 text-[26px]',
};

// Status dot colors
const STATUS_COLOR: Record<NonNullable<AvatarProps['status']>, string> = {
  online: 'var(--ollio-success)',
  away: 'var(--ollio-warning)',
  offline: 'var(--ollio-slate)',
};

export function Avatar({
  src,
  name = '',
  size = 'md',
  ring = false,
  status,
  className,
  ...rest
}: AvatarProps) {
  const label = name || 'avatar';

  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        // Layout: relative required for absolute status dot
        'relative inline-flex flex-none items-center justify-center rounded-full',
        // Gold wash + navy text
        'bg-[var(--ollio-gold-light)] text-[var(--ollio-navy)]',
        // Typography
        'font-sans font-semibold select-none',
        // Size
        SIZE_CLASS[size],
        // Gold ring (DS uses box-shadow form: inner card-offset + outer gold)
        ring && 'ring-2 ring-[var(--ollio-gold)] ring-offset-2 ring-offset-[var(--surface-card)]',
        className,
      )}
      {...rest}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className="h-full w-full object-cover rounded-full"
        />
      ) : (
        initials(name)
      )}

      {status !== undefined && (
        <span
          data-status={status}
          aria-hidden="true"
          className="absolute bottom-0 right-0 h-[26%] w-[26%] rounded-full border-2 border-[var(--surface-card)]"
          style={{ background: STATUS_COLOR[status] }}
        />
      )}
    </span>
  );
}
